// [MOBILE 2026-07-07] Single source of truth for "are we in the phone layout?".
// The app keeps its desktop layout untouched and swaps in a distinct mobile
// component tree below this width (see the research brief: build two complete
// layouts, one hard boundary, so there's no awkward tablet mid-zone). 768px is
// the tablet-portrait boundary — phones + small/portrait tablets get mobile.
//
// This is a JS switch (matchMedia), NOT a CSS `display:none` toggle, because the
// mobile tree renders genuinely different components (cards, not a <table>);
// hiding a mounted desktop table would waste work and still run its layout code.
// Keep this the ONLY place the breakpoint value lives so the JS hook and any CSS
// media queries never drift.
import { useEffect, useState } from 'react';

export const MOBILE_MAX_WIDTH = 768;
const QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;

function getMatch(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(QUERY).matches;
}

/** True when the viewport is at/below the mobile breakpoint. Updates on resize/rotate. */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(QUERY);
    const onChange = () => setIsMobile(mq.matches);
    onChange(); // sync in case it changed between first render and effect
    // addEventListener('change') is the modern API; older Safari needs addListener.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return isMobile;
}

export default useIsMobile;
